import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';
import { generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';

const ServicesPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  // Generate structured data
  const structuredData = [
    generateWebPageSchema(
      '事業内容｜おかやま放課後等デイサービス｜岡山市の療育・発達支援',
      '岡山市の放課後等デイサービス「おかやま放課後等デイサービス」の事業内容。個別支援計画、集団療育、学習支援、創作活動、運動療育、生活訓練など多様なプログラムを提供。',
      '/services'
    ),
    generateBreadcrumbSchema([
      { name: 'ホーム', url: '/' },
      { name: '事業内容', url: '/services' }
    ])
  ];

  const services = [
    {
      title: '個別支援計画の作成',
      description: 'お子様一人ひとりの発達段階や特性を詳しく把握し、専門スタッフが個別支援計画を作成します。定期的な見直しを行い、常に最適な支援を提供します。',
      image: 'https://public.readdy.ai/ai/img_res/aa5b691f32eb58675ef7b25aa7531af3.jpg',
      features: ['発達アセスメント', '目標設定', '支援方法の検討', '定期的な評価・見直し']
    },
    {
      title: '集団療育プログラム',
      description: '同年代のお子様との関わりを通じて、社会性やコミュニケーション能力を育みます。グループ活動を通じて協調性や自己表現力を身につけます。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20children%20participating%20in%20group%20therapy%20activities%2C%20sitting%20in%20circle%2C%20colorful%20educational%20materials%2C%20therapist%20facilitating%2C%20bright%20therapy%20room%2C%20inclusive%20environment%2C%20natural%20lighting&width=400&height=300&seq=service2&orientation=landscape',
      features: ['ソーシャルスキルトレーニング', 'グループワーク', 'コミュニケーション練習', '協調性の育成']
    },
    {
      title: '学習支援・宿題サポート',
      description: '学校の宿題サポートや基礎学習の定着を図ります。お子様の学習ペースに合わせて、丁寧に指導いたします。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20children%20doing%20homework%20with%20caring%20teacher%20support%2C%20bright%20study%20room%2C%20educational%20books%20and%20materials%2C%20focused%20learning%20atmosphere%2C%20comfortable%20seating%2C%20natural%20lighting&width=400&height=300&seq=service3&orientation=landscape',
      features: ['宿題サポート', '基礎学習指導', '学習習慣の確立', '個別指導']
    },
    {
      title: '創作・表現活動',
      description: '工作、絵画、音楽などの創作活動を通じて、創造性や集中力、達成感を育てます。お子様の興味や関心を大切にした活動を行います。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20children%20engaged%20in%20creative%20art%20activities%2C%20painting%20and%20crafting%2C%20colorful%20art%20supplies%2C%20bright%20art%20room%2C%20joyful%20expressions%2C%20creative%20atmosphere%2C%20natural%20lighting&width=400&height=300&seq=service4&orientation=landscape',
      features: ['工作活動', '絵画・造形', '音楽活動', '表現力の向上']
    },
    {
      title: '運動療育・体力づくり',
      description: '体を動かす楽しさを感じながら、基礎体力や運動能力の向上を図ります。バランス感覚や協調運動の発達を促します。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20children%20doing%20physical%20therapy%20exercises%2C%20movement%20activities%2C%20colorful%20exercise%20equipment%2C%20spacious%20gym%20room%2C%20active%20participation%2C%20safe%20environment%2C%20natural%20lighting&width=400&height=300&seq=service5&orientation=landscape',
      features: ['基礎体力向上', 'バランス訓練', '協調運動', '運動の楽しさ体験']
    },
    {
      title: '生活訓練・自立支援',
      description: '日常生活に必要なスキルを身につけ、自立に向けた支援を行います。身辺自立から社会生活スキルまで幅広くサポートします。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20children%20learning%20daily%20life%20skills%2C%20practicing%20self-care%20activities%2C%20bright%20training%20room%2C%20supportive%20staff%20guidance%2C%20practical%20learning%20environment%2C%20natural%20lighting&width=400&height=300&seq=service6&orientation=landscape',
      features: ['身辺自立訓練', '社会生活スキル', '金銭管理', '公共交通機関の利用']
    }
  ];

  const schedule = [
    { time: '14:00-15:00', activity: 'お迎え・到着', description: '学校へのお迎えと施設到着' },
    { time: '15:00-15:30', activity: 'おやつ・休憩', description: '手洗い・うがい、おやつタイム' },
    { time: '15:30-16:30', activity: '学習支援', description: '宿題サポート・個別学習' },
    { time: '16:30-17:30', activity: '療育活動', description: '個別・集団療育プログラム' },
    { time: '17:30-18:00', activity: 'お送り・お迎え', description: 'ご自宅への送迎・保護者お迎え' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="事業内容｜おかやま放課後等デイサービス｜岡山市の療育・発達支援"
        description="岡山市の放課後等デイサービス「おかやま放課後等デイサービス」の事業内容。個別支援計画、集団療育、学習支援、創作活動、運動療育、生活訓練など多様なプログラムを提供しています。"
        keywords="岡山市,放課後等デイサービス,事業内容,療育,発達支援,個別支援計画,集団療育"
        canonicalUrl={`${siteUrl}/services`}
        structuredData={structuredData}
      />
      
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 md:py-24 bg-gradient-to-br from-blue-600 to-green-600">
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 md:mb-8">
            <strong>事業内容</strong>
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed">
            お子様一人ひとりの個性を大切にし、楽しく学べる環境で成長をサポートする放課後等デイサービスです。
          </p>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 md:mb-6">
              <strong>詳細なサービス内容</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              発達支援の専門知識を持つスタッフが、お子様の成長段階に応じた多様なプログラムを提供します。
            </p>
          </div>
          
          <div className="space-y-12 md:space-y-16">
            {services.map((service, index) => (
              <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 lg:gap-12`}>
                <div className="w-full lg:w-1/2">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300"
                  />
                </div>
                <div className="w-full lg:w-1/2 space-y-4 md:space-y-6">
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 break-words">{service.title}</h3>
                  <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed break-words">{service.description}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center gap-3">
                        <i className="ri-check-line text-green-500 text-xl flex-shrink-0"></i>
                        <span className="text-sm sm:text-base md:text-lg text-gray-700 break-words">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Daily Schedule Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 md:mb-6">
              <strong>一日のスケジュール</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600">
              お子様が安心して過ごせるよう、規則正しいスケジュールで活動しています。
            </p>
          </div>
          
          <div className="space-y-4 md:space-y-6">
            {schedule.map((item, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 md:p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 md:gap-6">
                  <div className="sm:w-36 flex-shrink-0">
                    <span className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm md:text-base font-semibold whitespace-nowrap shadow-sm">
                      {item.time}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-2 break-words">{item.activity}</h4>
                    <p className="text-sm sm:text-base md:text-lg text-gray-600 break-words">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 md:mb-6">
              <strong>私たちの特徴</strong>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <div className="text-center hover:-translate-y-2 transition-all duration-300">
              <div className="w-20 h-20 md:w-24 md:h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                <i className="ri-user-heart-fill text-3xl md:text-4xl text-blue-600"></i>
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-800 mb-4">個別対応</h3>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed break-words">お子様一人ひとりの特性に合わせたオーダーメイドの支援を提供します。</p>
            </div>
            
            <div className="text-center hover:-translate-y-2 transition-all duration-300">
              <div className="w-20 h-20 md:w-24 md:h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                <i className="ri-shield-check-fill text-3xl md:text-4xl text-green-600"></i>
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-800 mb-4">安心・安全</h3>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed break-words">お子様が安心して過ごせる環境づくりと安全管理を徹底しています。</p>
            </div>
            
            <div className="text-center hover:-translate-y-2 transition-all duration-300">
              <div className="w-20 h-20 md:w-24 md:h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                <i className="ri-team-fill text-3xl md:text-4xl text-purple-600"></i>
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-800 mb-4">専門スタッフ</h3>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed break-words">経験豊富な専門スタッフがお子様の成長をサポートします。</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
